import {
  AboutAptosConnect,
  AboutAptosConnectEducationScreen,
  AptosPrivacyPolicy,
  WalletItem,
  WalletSortingOptions,
  groupAndSortWallets,
  isInstallRequired,
  useWallet,
  topoStandardSupportedWalletList,
  AdapterWallet,
} from "@topo/wallet-adapter-react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  Divider,
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {grey} from "./aptosColorPalette";
// reported bug with loading mui icons with esm, therefore need to import like this https://github.com/mui/material-ui/issues/35233
import {ArrowBack, ArrowForward} from "@mui/icons-material";
import {RiArrowDownSLine, RiArrowUpSLine, RiCloseLine} from "@remixicon/react";
import {JSX, useState} from "react";
import {WalletConnectorProps} from "./WalletConnector";

interface WalletsModalProps
  extends Pick<WalletConnectorProps, "networkSupport" | "modalMaxWidth">,
    WalletSortingOptions {
  handleClose: () => void;
  modalOpen: boolean;
}

export default function WalletsModal({
  handleClose,
  modalOpen,
  networkSupport,
  modalMaxWidth,
  ...walletSortingOptions
}: WalletsModalProps): JSX.Element {
  const theme = useTheme();
  const [showMoreWallets, setShowMoreWallets] = useState(false);
  const closeModal = () => {
    setShowMoreWallets(false);
    handleClose();
  };

  const {wallets: installedWallets = []} = useWallet();
  const wallets: AdapterWallet[] = [...installedWallets];
  const installedWalletNames = wallets.map((wallet) => wallet.name);
  for (const wallet of topoStandardSupportedWalletList) {
    if (!installedWalletNames.includes(wallet.name)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      wallets.push(wallet as any); // TODO: Make this typed properly
    }
  }

  const {aptosConnectWallets, availableWallets, installableWallets} =
    groupAndSortWallets([...wallets], walletSortingOptions);

  // Filter out "T wallet" from available wallets
  const filteredAvailableWallets = availableWallets.filter(
    (wallet) => wallet.name !== "T wallet",
  );

  const hasAptosConnectWallets = false;

  return (
    <Dialog
      open={modalOpen}
      onClose={closeModal}
      aria-label="wallet selector modal"
      maxWidth={modalMaxWidth ?? "xs"}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: 480,
            maxWidth: "calc(100% - 32px)",
            m: 2,
            borderRadius: "24px",
            backgroundColor: theme.palette.tokens.layer.modal,
            maxHeight: "calc(100% - 32px)",
            overflow: "hidden",
          },
        },
      }}
    >
      <Stack
        sx={{
          bgcolor: theme.palette.tokens.layer.modal,
          p: 4,
          gap: 0,
          maxHeight: "calc(100dvh - 32px)",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={closeModal}
          sx={{
            position: "absolute",
            right: 28,
            top: 32,
            p: 0,
            color: theme.palette.tokens.text.tertiary,
            "&:hover": {backgroundColor: "transparent"},
          }}
        >
          <RiCloseLine size={20} />
        </IconButton>
        <AboutAptosConnect renderEducationScreen={renderEducationScreen}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              maxHeight: "calc(100dvh - 96px)",
            }}
          >
            <Box sx={{mb: 2, flexShrink: 0}}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  fontSize: "20px !important",
                  fontWeight: 600,
                  lineHeight: "24px",
                }}
              >
                {hasAptosConnectWallets ? (
                  <>
                    <span>Log in or sign up</span>
                    <span>with Social + Aptos Connect</span>
                  </>
                ) : (
                  "Connect Wallet"
                )}
              </Typography>
              {networkSupport && (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: "14px !important",
                    lineHeight: "18px",
                    color: theme.palette.tokens.text.tertiary,
                  }}
                >
                  {networkSupport.charAt(0).toUpperCase() +
                    networkSupport.slice(1)}{" "}
                  only
                </Typography>
              )}
            </Box>
            <Box
              data-testid="wallet-list-scroll"
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                overflowY: "auto",
                overscrollBehavior: "contain",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              {hasAptosConnectWallets && (
                <Stack gap={1}>
                  {aptosConnectWallets.map((wallet) => (
                    <AptosConnectWalletRow
                      key={wallet.name}
                      wallet={wallet}
                      onConnect={closeModal}
                    />
                  ))}
                  <Typography
                    component="p"
                    fontSize="14px"
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      justifyContent: "center",
                      alignItems: "center",
                      color: grey[400],
                    }}
                  >
                    Learn more about{" "}
                    <Box
                      component={AboutAptosConnect.Trigger}
                      sx={{
                        background: "none",
                        border: "none",
                        fontFamily: "inherit",
                        fontSize: "inherit",
                        cursor: "pointer",
                        display: "flex",
                        gap: 0.5,
                        px: 0,
                        py: 1.5,
                        alignItems: "center",
                        color: theme.palette.text.primary,
                        appearance: "none",
                      }}
                    >
                      Aptos Connect{" "}
                      <ArrowForward sx={{height: 16, width: 16}} />
                    </Box>
                  </Typography>

                  <Box
                    component={AptosPrivacyPolicy}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      py: 0.5,
                    }}
                  >
                    <Typography component="p" fontSize="12px" lineHeight="20px">
                      <AptosPrivacyPolicy.Disclaimer />{" "}
                      <Box
                        component={AptosPrivacyPolicy.Link}
                        sx={{
                          color: grey[400],
                          textDecoration: "underline",
                          textUnderlineOffset: "4px",
                        }}
                      />
                      <span>.</span>
                    </Typography>
                    <Box
                      component={AptosPrivacyPolicy.PoweredBy}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        fontSize: "12px",
                        lineHeight: "20px",
                        color: grey[400],
                      }}
                    />
                  </Box>
                  <Divider sx={{color: grey[400], pt: 2}}>Or</Divider>
                </Stack>
              )}
              <Stack sx={{gap: 1}}>
                {filteredAvailableWallets.map((wallet) => (
                  <WalletRow
                    key={wallet.name}
                    wallet={wallet}
                    onConnect={closeModal}
                  />
                ))}
              </Stack>
              {!!installableWallets.length && (
                <>
                  <Button
                    onClick={() => setShowMoreWallets((expanded) => !expanded)}
                    aria-expanded={showMoreWallets}
                    sx={{
                      width: "100%",
                      height: 56,
                      justifyContent: "center",
                      color: theme.palette.tokens.text.primary,
                      fontSize: 16,
                      fontWeight: 500,
                      textTransform: "none",
                      backgroundColor: "transparent",
                      "& .MuiButton-endIcon": {ml: 0.5},
                      "&:hover": {
                        backgroundColor: "transparent",
                        filter: "none",
                      },
                    }}
                    endIcon={
                      showMoreWallets ? (
                        <RiArrowUpSLine size={18} />
                      ) : (
                        <RiArrowDownSLine size={18} />
                      )
                    }
                  >
                    More Wallets
                  </Button>
                  <Collapse in={showMoreWallets} unmountOnExit>
                    <Stack sx={{gap: 1}}>
                      {installableWallets.map((wallet) => (
                        <WalletRow
                          key={wallet.name}
                          wallet={wallet}
                          onConnect={closeModal}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </>
              )}
            </Box>
          </Box>
        </AboutAptosConnect>
      </Stack>
    </Dialog>
  );
}

interface WalletRowProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  wallet: any;
  onConnect?: () => void;
}

function WalletRow({wallet, onConnect}: WalletRowProps) {
  const theme = useTheme();
  return (
    <WalletItem wallet={wallet} onConnect={onConnect} asChild>
      <ListItem disablePadding>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            px: 2,
            minHeight: 72,
            gap: 1.5,
            border: "solid 1px",
            borderColor: theme.palette.tokens.line.subtle,
            borderRadius: `${theme.shape.borderRadius}px`,
          }}
        >
          <Box
            component={WalletItem.Icon}
            sx={{width: 36, height: 36, borderRadius: "8px"}}
          />
          <ListItemText
            primary={wallet.name}
            sx={{m: 0}}
            primaryTypographyProps={{fontSize: 16, lineHeight: "24px"}}
          />
          {isInstallRequired(wallet) ? (
            <WalletItem.InstallLink asChild>
              <Button
                LinkComponent={"a"}
                size="small"
                className="wallet-connect-install"
                sx={{
                  width: 89,
                  minWidth: 89,
                  height: 32,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 400,
                  color: "var(---text-primary, #151517)",
                  backgroundColor: "var(---bg-secondary, #F2F2F5)",
                  "&:hover": {
                    backgroundColor: "var(---bg-tertiary, #DEDFE0)",
                    filter: "none",
                  },
                }}
              >
                Install
              </Button>
            </WalletItem.InstallLink>
          ) : (
            <WalletItem.ConnectButton asChild>
              <Button
                variant="contained"
                size="small"
                className="wallet-connect-button"
                sx={{
                  width: 89,
                  minWidth: 89,
                  height: 32,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 400,
                  color: theme.palette.primary.contrastText,
                  backgroundColor: theme.palette.tokens.function.primary,
                  "&:hover": {
                    backgroundColor: theme.palette.tokens.function.primaryClick,
                    filter: "none",
                  },
                }}
              >
                Connect
              </Button>
            </WalletItem.ConnectButton>
          )}
        </Box>
      </ListItem>
    </WalletItem>
  );
}

function AptosConnectWalletRow({wallet, onConnect}: WalletRowProps) {
  return (
    <WalletItem wallet={wallet} onConnect={onConnect} asChild>
      <WalletItem.ConnectButton asChild>
        <Button
          size="large"
          variant="outlined"
          sx={{display: "flex", alignItems: "center", gap: 1.5}}
        >
          <Box component={WalletItem.Icon} sx={{width: 20, height: 20}} />
          <WalletItem.Name />
        </Button>
      </WalletItem.ConnectButton>
    </WalletItem>
  );
}

function renderEducationScreen(screen: AboutAptosConnectEducationScreen) {
  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 4fr 1fr",
          alignItems: "center",
          justifyItems: "start",
        }}
      >
        <IconButton onClick={screen.cancel}>
          <ArrowBack />
        </IconButton>
        <Typography variant="body1" component="h2" width="100%" align="center">
          About Aptos Connect
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          pb: 1.5,
          alignItems: "end",
          justifyContent: "center",
          height: "162px",
        }}
      >
        <screen.Graphic />
      </Box>
      <Stack sx={{gap: 1, textAlign: "center", pb: 2}}>
        <Typography component={screen.Title} variant="h6" />
        <Typography
          component={screen.Description}
          variant="body2"
          sx={{
            "&>a": {
              color: (theme) => theme.palette.text.primary,
              textDecoration: "underline",
              textUnderlineOffset: "4px",
            },
          }}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          alignItems: "center",
        }}
      >
        <Button
          size="small"
          variant="text"
          onClick={screen.back}
          sx={{justifySelf: "start"}}
        >
          Back
        </Button>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            placeSelf: "center",
          }}
        >
          {screen.screenIndicators.map((ScreenIndicator, i) => (
            <Box
              key={i}
              component={ScreenIndicator}
              sx={{
                px: 0,
                py: 2,
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  height: "2px",
                  width: "24px",
                  bgcolor: (theme) => theme.palette.text.disabled,
                  "[data-active]>&": {
                    bgcolor: (theme) => theme.palette.text.primary,
                  },
                }}
              />
            </Box>
          ))}
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={screen.next}
          sx={{justifySelf: "end"}}
          endIcon={<ArrowForward sx={{height: 16, width: 16}} />}
        >
          {screen.screenIndex === screen.totalScreens - 1 ? "Finish" : "Next"}
        </Button>
      </Box>
    </>
  );
}
